import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchCollection, fetchCaseBySlug, getOptimizedImageUrl } from '../lib/cms';
import { ArrowLeft, ChevronRight, CheckCircle2, Shield, Calendar, Tag } from 'lucide-react';

export default function Cases({ isEn }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const lang = isEn ? 'en' : 'da';

  const [cases, setCases] = useState([]);
  const [currentCase, setCurrentCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        if (slug) {
          const item = await fetchCaseBySlug(slug);
          if (item) {
            setCurrentCase(item);
          } else {
            // Hvis casen ikke findes, omdiriger til 404
            navigate(isEn ? '/en/404' : '/404');
          }
        } else {
          const items = await fetchCollection('cases', { limit: 100 });
          setCases(items);
          setCurrentCase(null);
        }
      } catch (error) {
        console.error('Error loading cases:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [slug, navigate, isEn]);

  // Filtrering af cases
  const filteredCases = activeFilter === 'all' 
    ? cases 
    : cases.filter(c => {
        const b = c.branche?.[lang]?.toLowerCase() || '';
        return b.includes(activeFilter.toLowerCase());
      });

  // 1. DETALJESIDE
  if (slug && currentCase) {
    const title = currentCase.title?.[lang] || '';
    const desc = currentCase.description?.[lang] || '';
    const res = currentCase.resultat?.[lang] || '';
    const branch = currentCase.branche?.[lang] || '';
    const seoTitle = currentCase.seo_title?.[lang] || `${title} - Fredericia Isolerings-puder`;
    const seoDesc = currentCase.seo_description?.[lang] || '';

    return (
      <>
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDesc} />
        </Helmet>

        <article className="py-12 sm:py-20 bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back Link */}
            <Link
              to={isEn ? '/en/cases' : '/cases'}
              className="inline-flex items-center gap-2 text-sm font-mono text-black hover:text-brand-accent transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" />
              {isEn ? 'Back to Case Studies' : 'Tilbage til cases'}
            </Link>

            {/* Header */}
            <div className="space-y-4 text-left mb-12">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 tracking-tight leading-tight">
                {title}
              </h1>
            </div>

            {/* Main Image */}
            {currentCase.images?.[0] && (
              <div className="aspect-[16/10] rounded-none overflow-hidden border border-gray-200 shadow-sm mb-12">
                <img
                  src={getOptimizedImageUrl(currentCase.images[0], { width: 1200 })}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Content Grid */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-12 text-left">
              {/* Left Column: Description */}
              <div className={`${res ? 'md:col-span-8' : 'md:col-span-12'} space-y-8`}>
                <div className="space-y-4">
                  <h2 className="text-xl sm:text-2xl font-display font-bold text-gray-900 border-b border-gray-200 pb-2">
                    {isEn ? 'Challenge & Solution' : 'Udfordring & Løsning'}
                  </h2>
                  <div 
                    className="text-gray-700 space-y-4 leading-relaxed text-sm sm:text-base prose"
                    dangerouslySetInnerHTML={{ __html: desc }}
                  />
                </div>
              </div>

              {/* Right Column: Results Sidebar */}
              {res && (
                <div className="md:col-span-4 space-y-6">
                  <div className="bg-white border border-gray-200 rounded-none p-6 space-y-4 shadow-sm">
                    <h3 className="font-display font-bold text-gray-900 text-lg tracking-tight border-b border-gray-200 pb-2 flex items-center gap-2">
                      <Shield className="h-5 w-5 text-brand-accent" />
                      {isEn ? 'Results' : 'Resultat'}
                    </h3>
                    <div 
                      className="text-sm text-gray-700 leading-relaxed space-y-2 prose"
                      dangerouslySetInnerHTML={{ __html: res }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Extra Gallery if more images exist */}
            {currentCase.images && currentCase.images.length > 1 && (
              <div className="mt-16 space-y-6 text-left">
                <h2 className="text-xl sm:text-2xl font-display font-bold text-gray-900 border-b border-gray-200 pb-2">
                  {isEn ? 'Project Gallery' : 'Galleri'}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                  {currentCase.images.slice(1).map((imgUrl, idx) => (
                    <div 
                      key={idx} 
                      className="aspect-square rounded-none overflow-hidden border border-gray-200 bg-white group hover:border-gray-300 transition-all duration-300"
                    >
                      <img
                        src={getOptimizedImageUrl(imgUrl, { width: 400, height: 400, fit: 'cover' })}
                        alt={`${title} - Galleri ${idx + 1}`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </>
    );
  }

  // 2. OVERSIGTSSIDE
  const seoTitle = isEn ? 'Case Studies - Fredericia Isolerings-puder' : 'Cases - Fredericia Isolerings-puder';
  const seoDesc = isEn 
    ? 'Explore our documented results and energy optimization case studies.' 
    : 'Udforsk vores dokumenterede resultater og cases inden for energioptimering.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
      </Helmet>

      <section className="py-12 sm:py-20 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-white tracking-tight">
              {isEn ? 'Case Studies' : 'Cases'}
            </h1>
          </div>

          {/* Filter Controls */}
          <div className="flex justify-center gap-3 mb-12">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-4 py-2 rounded-none text-xs font-mono uppercase tracking-wider border transition-all duration-200 ${
                activeFilter === 'all'
                  ? 'bg-brand-accent border-brand-accent text-white font-bold'
                  : 'border-gray-800 hover:border-gray-700 text-gray-400 hover:text-white'
              }`}
            >
              {isEn ? 'All' : 'Alle'}
            </button>
            <button
              onClick={() => setActiveFilter('industri')}
              className={`px-4 py-2 rounded-none text-xs font-mono uppercase tracking-wider border transition-all duration-200 ${
                activeFilter === 'industri'
                  ? 'bg-brand-accent border-brand-accent text-white font-bold'
                  : 'border-gray-800 hover:border-gray-700 text-gray-400 hover:text-white'
              }`}
            >
              {isEn ? 'Industry' : 'Industri'}
            </button>
            <button
              onClick={() => setActiveFilter('marine')}
              className={`px-4 py-2 rounded-none text-xs font-mono uppercase tracking-wider border transition-all duration-200 ${
                activeFilter === 'marine'
                  ? 'bg-brand-accent border-brand-accent text-white font-bold'
                  : 'border-gray-800 hover:border-gray-700 text-gray-400 hover:text-white'
              }`}
            >
              {isEn ? 'Marine' : 'Marine'}
            </button>
          </div>

          {/* Cases Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent" />
            </div>
          ) : filteredCases.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-left">
              {filteredCases.map((item) => (
                <Link
                  key={item.id}
                  to={isEn ? `/en/cases/${item.url_slug}` : `/cases/${item.url_slug}`}
                  className="bg-brand-surface border border-gray-800 rounded-none overflow-hidden group hover:border-gray-700 transition-all duration-300 flex flex-col"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-brand-dark">
                    {item.images?.[0] ? (
                      <img
                        src={getOptimizedImageUrl(item.images[0], { width: 400 })}
                        alt={item.title?.[lang]}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-600 font-mono text-xs">
                        {isEn ? 'No Image' : 'Intet billede'}
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex-grow flex flex-col justify-between space-y-4">
                    <div className="space-y-2">
                      <h3 className="font-display font-bold text-lg text-white group-hover:text-brand-accent transition-colors line-clamp-2">
                        {item.title?.[lang]}
                      </h3>
                      <div 
                        className="text-xs text-gray-400 line-clamp-3 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: item.description?.[lang] || '' }}
                      />
                    </div>
                    <div className="text-xs font-semibold text-white flex items-center gap-1">
                      {isEn ? 'Read Case Study' : 'Læs hele casen'}
                      <ChevronRight className="h-3 w-3" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-gray-500 font-mono text-xs">
              {isEn ? 'No case studies found.' : 'Ingen cases fundet.'}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
